import React, {useState} from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import * as dayjs from 'dayjs'
import * as utc from 'dayjs/plugin/utc'
import * as timezone from 'dayjs/plugin/timezone'
import * as relativeTime from 'dayjs/plugin/relativeTime'
import {ActionButton, Alert, FormGroup, useAlert} from "./components/FormComps"
import {useIntlEx} from "./components/IntlUtils.js"
import {useSolv} from "./components/SolvProvider";
import {closeDialog, ModalDialog} from "./components/DialogUtils";
import {mkHref} from "./components/AccountUtils";
import {default as Select} from "react-select";
import {MarkdownTextArea} from "./components/MarkdownTextArea";
import {InfoBlock, InfoText} from "./components/ValidationUtils";
import getLogger from "./components/Logging.js"
import './App.css'

const log = getLogger("BroadcastStopDialog")

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

export default function BroadcastStopDialog(props)  {

  const {api, session, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const [alert, setAlert] = useAlert()
  const [touched, setTouched] = useState(false)

  const stopStatusOpts = [
    {
      label: <><div>{intl.msg("broadcast_stop_status_ended")}</div><small>{intl.msg("broadcast_stop_status_ended_description")}</small></>,
      value: "ENDED"
    },
    {
      label: <><div>{intl.msg("broadcast_stop_status_stopped")}</div><small>{intl.msg("broadcast_stop_status_stopped_description")}</small></>,
      value: "STOPPED"
    },
  ]

  const [stopStatus, setStopStatus] = useState(null)
  const [description, setDescription] = useState(null)
  const [sendNotification, setSendNotification] = useState(true)

  function handleStopClick() {
    // log.debug("handleStopClick: invoked")
    try {
      let payload = {}
      if (isAccessingAsManager()) {
        payload = {
          status: stopStatus.value,
          description: description, // || intl.msg("credit_transaction_description_return", {broadcastName: props.broadcastName}),
        }
      }
      else {
        payload = {
          status: 'STOPPED',
        }
      }
      setBusy(intl.msg("working"))
      api.stopBroadcast(props.tenant.tenantId, props.broadcastId, payload)
        .then((data) => {
          window.location = mkHref({suffix: `/broadcasts`, tenant: props.tenant, user: session.user})
        })
        .catch((error) => {
          log.error("ERROR!!!!", error)
          handleError(error)
          setAlert({type: "error", text: intl.msg("error_failed")})
        })
        .finally(() => {
          setBusy(null)
        })
    }
    catch (error) {
      // log.debug("ERROR!!!!", error)
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function handleError(error) {
    if (error && error.code) {
      switch (error.code) {
        case "ALREADY_REFUNDED":
          setAlert({type: "error", text: intl.msg("broadcast_stop_error_already_refunded")})
          break
        case "UNKNOWN_BROADCAST":
          setAlert({type: "error", text: intl.msg("broadcast_stop_error_unknown_broadcast")})
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed")})
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function handleOpenDialog() {
    setAlert(null)
  }

  function handleCloseDialog() {
    closeDialog("dlg_broadcast_stop")
  }

  function handleStopStatusChange(v) {
    setStopStatus(v)
    setTouched(true)
  }

  function handleDescriptionChange(e) {
    setDescription(e.target.value)
    setTouched(true)
  }

  function handleSendNotificationChange(v) {
    setSendNotification(v)
    setTouched(true)
  }

  function isAccessingAsMember() {
    return props.tenant.accessingAs("*/INSTREAM/*")
  }

  function isAccessingAsManager() {
    return props.tenant.accessingAs("*/DOWNSTREAM/*")
  }

  return (

    props.tenant &&
      <ModalDialog
        dialogId="dlg_broadcast_stop"
        size={isAccessingAsMember() ? "md" : "xl"} keyboard={"true"} backdrop={props.backdrop || "static"}
        onOpen={handleOpenDialog}
        onClose={handleCloseDialog}>

        <ModalDialog.Header
          title={intl.msg("broadcast_stop_title")}
          description={intl.msg("broadcast_stop_subtitle")}
          alert={alert}
        />

        <ModalDialog.Body>

          <Alert type="warning" title={intl.msg("broadcast_stop_alert_title", {broadcastName: props.broadcastName})} text={intl.msg(`broadcast_stop_alert_text_${isAccessingAsManager() ? "manager" : "member"}`, {broadcastName: props.broadcastName})}/>

          {
            isAccessingAsManager() &&
              <div className="mt-3">

                <FormGroup>
                  <FormGroup.Label htmlFor="inp_stopStatus" text={intl.msg("broadcast_stop_status")} description={intl.msg("broadcast_stop_status_description")}/>
                  <FormGroup.Control>
                    <Select
                      id="inp_stopStatus"
                      className="react-select"
                      classNamePrefix="react-select"
                      value={stopStatus}
                      options={stopStatusOpts}
                      onChange={handleStopStatusChange}
                      placeholder={null}
                      isSearchable={false}
                      isDisabled={false}/>
                    <InfoBlock>
                      <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                {
                  "STOPPED" === stopStatus?.value &&
                    <FormGroup className="mt-3">
                      <FormGroup.Label htmlFor="inp_description" text={intl.msg("broadcast_stop_description")} description={intl.msg("broadcast_stop_description_description")}/>
                      <FormGroup.Controls>
                        <MarkdownTextArea id="inp_description" value={description} onChange={handleDescriptionChange}>
                          <InfoBlock>
                            <InfoText>{intl.msg("helptext_you_can_use_markdown")}</InfoText>
                            <InfoText required>{intl.msg("helptext_required")}</InfoText>
                          </InfoBlock>
                        </MarkdownTextArea>
                      </FormGroup.Controls>
                    </FormGroup>
                }

              </div>
          }
        </ModalDialog.Body>

        <ModalDialog.Footer>
          <ModalDialog.Footer.Controls>
            <ActionButton name="cancel" onClick={handleCloseDialog}/>
            <ActionButton name="stop" confirm={intl.msg("confirm_stop")} flavor="primary" disabled={isAccessingAsManager() ? (!stopStatus || ("STOPPED" === stopStatus.value && !description)) : false} onClick={handleStopClick}/>
          </ModalDialog.Footer.Controls>
        </ModalDialog.Footer>

      </ModalDialog>
  )
}